import React, { useState, useEffect, useRef } from 'react'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore.js'

const JurnalUmum = () => {
  const { user } = useAuthStore()
  const [formData, setFormData] = useState({
    TglJurnal: new Date().toISOString().split('T')[0],
    JenisJurnal: 'Umum',
    Keterangan: ''
  })
  const [details, setDetails] = useState([
    { KodeAkun: '', Debet: 0, Kredit: 0, Keterangan: '' }
  ])
  const [coaList, setCoaList] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const coaSelectRefs = useRef([])

  useEffect(() => {
    fetchCOA()
  }, [])

  useEffect(() => {
    // Initialize Select2 for all account dropdowns
    coaSelectRefs.current.forEach((ref, index) => {
      if (ref) {
        // Check if Select2 is already initialized
        if ($(ref).data('select2')) {
          $(ref).select2('destroy')
        }
        
        // Initialize Select2
        $(ref).select2({
          placeholder: '-- Pilih Akun --',
          allowClear: true,
          width: '100%'
        })

        // Set current value
        if (details[index]?.KodeAkun) {
          $(ref).val(details[index].KodeAkun).trigger('change')
        }

        // Handle change event
        $(ref).on('change', (e) => {
          handleDetailChange(index, 'KodeAkun', e.target.value)
        })
      }
    })

    // Cleanup
    return () => {
      coaSelectRefs.current.forEach(ref => {
        if (ref && $(ref).data('select2')) {
          $(ref).off('change').select2('destroy')
        }
      })
    }
  }, [coaList, details.length])

  const fetchCOA = async () => {
    try {
      const response = await api.get('/settings/coa')
      setCoaList(Array.isArray(response.data.data?.data) ? response.data.data.data : [])
    } catch (error) {
      console.error('Error fetching COA:', error)
      setError('Gagal memuat daftar akun')
      setCoaList([])
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleDetailChange = (index, field, value) => {
    const newDetails = [...details]
    newDetails[index][field] = value
    setDetails(newDetails)
  }

  const addDetail = () => {
    setDetails([...details, { KodeAkun: '', Debet: 0, Kredit: 0, Keterangan: '' }])
  }

  const removeDetail = (index) => {
    if (details.length > 1) {
      // Destroy Select2 for the removed ref
      if (coaSelectRefs.current[index]) {
        $(coaSelectRefs.current[index]).select2('destroy')
      }
      
      const newDetails = details.filter((_, i) => i !== index)
      setDetails(newDetails)
      
      // Update refs array
      coaSelectRefs.current = coaSelectRefs.current.filter((_, i) => i !== index)
    }
  }

  const calculateTotals = () => {
    const totalDebet = details.reduce((sum, d) => sum + parseFloat(d.Debet || 0), 0)
    const totalKredit = details.reduce((sum, d) => sum + parseFloat(d.Kredit || 0), 0)
    return { totalDebet, totalKredit, balance: totalDebet - totalKredit }
  }

  const { totalDebet, totalKredit, balance } = calculateTotals()
  const [transactionData, setTransactionData] = useState(null)
  const [showConfirmation, setShowConfirmation] = useState(false)

  const handlePrint = () => {
    if (!transactionData) return

    // Format for PLQ20/draft printer (80mm width)
    const receiptContent = `
================================
  BUKTI JURNAL UMUM
================================
No Jurnal : ${transactionData.noJurnal}
Tanggal   : ${new Date(transactionData.TglJurnal).toLocaleDateString('id-ID')}
Jenis     : ${transactionData.JenisJurnal}
${transactionData.namaKoperasi || 'Koperasi'}
--------------------------------
KETERANGAN
--------------------------------
${transactionData.Keterangan}
--------------------------------
DETAIL JURNAL
--------------------------------
${transactionData.details.map(d => {
  const coa = coaList.find(c => c.KodeAkun === d.KodeAkun)
  return `${d.KodeAkun} - ${coa?.NamaAkun || '-'}
Debet : Rp ${parseFloat(d.Debet).toLocaleString('id-ID')}
Kredit: Rp ${parseFloat(d.Kredit).toLocaleString('id-ID')}
${d.Keterangan ? `Catatan: ${d.Keterangan}` : ''}`
}).join('\n--------------------------------\n')}
--------------------------------
Total Debet : Rp ${transactionData.totalDebet.toLocaleString('id-ID')}
Total Kredit: Rp ${transactionData.totalKredit.toLocaleString('id-ID')}
================================
  Tanda Tangan

(${transactionData.namaKoperasi || 'Koperasi'})
================================
`

    const printWindow = window.open('', '_blank')
    printWindow.document.write(`
      <html>
        <head>
          <title>Bukti Jurnal Umum</title>
          <style>
            body {
              font-family: 'Courier New', monospace;
              font-size: 12px;
              margin: 0;
              padding: 10px;
              white-space: pre;
              line-height: 1.2;
            }
          </style>
        </head>
        <body>${receiptContent}</body>
      </html>
    `)
    printWindow.document.close()
    printWindow.print()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.TglJurnal || !formData.JenisJurnal || !formData.Keterangan) {
      setError('Mohon lengkapi semua field')
      return
    }

    if (details.length === 0) {
      setError('Mohon tambahkan minimal satu detail jurnal')
      return
    }

    for (const detail of details) {
      if (!detail.KodeAkun) {
        setError('Mohon pilih akun untuk semua detail')
        return
      }
      if (parseFloat(detail.Debet || 0) === 0 && parseFloat(detail.Kredit || 0) === 0) {
        setError('Mohon isi Debet atau Kredit untuk semua detail')
        return
      }
    }

    if (Math.abs(balance) > 0.01) {
      setError('Total Debet harus sama dengan Total Kredit')
      return
    }

    setLoading(true)
    try {
      const response = await api.post('/journal', {
        TglJurnal: formData.TglJurnal,
        JenisJurnal: formData.JenisJurnal,
        Keterangan: formData.Keterangan,
        details: details
      })
      setTransactionData({
        ...response.data.data,
        details: details,
        totalDebet: totalDebet,
        totalKredit: totalKredit
      })
      setShowConfirmation(true)
      // Reset form
      setFormData({
        TglJurnal: new Date().toISOString().split('T')[0],
        JenisJurnal: 'Umum',
        Keterangan: ''
      })
      setDetails([{ KodeAkun: '', Debet: 0, Kredit: 0, Keterangan: '' }])
      setError(null)
    } catch (error) {
      console.error('Error creating journal:', error)
      setError(error.response?.data?.message || 'Gagal membuat jurnal')
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setTransactionData(null)
    setShowConfirmation(false)
    setSuccess(null)
  }

  return (
    <div className="container-fluid p-3">
      <div className="card shadow border-0">
        <div className="card-header bg-primary text-white">
          <h5 className="m-0 font-weight-bold">Jurnal Umum</h5>
        </div>
        <div className="card-body">
          {showConfirmation && transactionData ? (
            <div className="alert alert-success">
              <h5 className="alert-heading"><i className="fas fa-check-circle"></i> Jurnal Berhasil Dibuat</h5>
              <p>Jurnal umum telah berhasil disimpan dengan No. <strong>{transactionData.noJurnal}</strong></p>
              <div className="mt-3">
                <button className="btn btn-primary" onClick={handlePrint}><i className="fas fa-print mr-2"></i>Cetak Bukti</button>
                <button className="btn btn-secondary ml-2" onClick={handleReset}><i className="fas fa-redo mr-2"></i>Transaksi Baru</button>
              </div>
            </div>
          ) : (
            <>
              {error && <div className="alert alert-danger">{error}</div>}
              {success && <div className="alert alert-success">{success}</div>}

              <form onSubmit={handleSubmit}>
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">Tanggal Jurnal</label>
                <input
                  type="date"
                  className="form-control"
                  name="TglJurnal"
                  value={formData.TglJurnal}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Jenis Jurnal</label>
                <select
                  className="form-control"
                  name="JenisJurnal"
                  value={formData.JenisJurnal}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Umum">Umum</option>
                  <option value="Koreksi">Koreksi</option>
                  <option value="Penutupan">Penutupan</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">No. Referensi</label>
                <input
                  type="text"
                  className="form-control"
                  value="Otomatis"
                  disabled
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Keterangan</label>
              <textarea
                className="form-control"
                name="Keterangan"
                value={formData.Keterangan}
                onChange={handleInputChange}
                rows="2"
                required
              />
            </div>

            <h6 className="mt-4 mb-3">Detail Jurnal</h6>
            
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '25%' }}>Kode Akun</th>
                    <th style={{ width: '15%' }}>Debet</th>
                    <th style={{ width: '15%' }}>Kredit</th>
                    <th style={{ width: '30%' }}>Keterangan</th>
                    <th style={{ width: '15%' }}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {details.map((detail, index) => (
                    <tr key={index}>
                      <td>
                        <select
                          ref={el => coaSelectRefs.current[index] = el}
                          className="form-select"
                          required
                        >
                          <option value="">Pilih Akun</option>
                          {coaList.map(coa => (
                            <option key={coa.KodeAkun} value={coa.KodeAkun}>
                              {coa.KodeAkun} - {coa.NamaAkun}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control"
                          value={detail.Debet}
                          onChange={(e) => handleDetailChange(index, 'Debet', e.target.value)}
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control"
                          value={detail.Kredit}
                          onChange={(e) => handleDetailChange(index, 'Kredit', e.target.value)}
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          value={detail.Keterangan}
                          onChange={(e) => handleDetailChange(index, 'Keterangan', e.target.value)}
                          placeholder="Keterangan detail"
                        />
                      </td>
                      <td>
                        {details.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => removeDetail(index)}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-light">
                  <tr>
                    <td colSpan="4" className="text-end">
                      <strong>Total:</strong>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-success"
                        onClick={addDetail}
                      >
                        <i className="fas fa-plus"></i> Tambah
                      </button>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="row mt-3">
              <div className="col-md-4">
                <div className="card">
                  <div className="card-body text-center">
                    <h6 className="card-title">Total Debet</h6>
                    <h4 className="text-primary">Rp {totalDebet.toLocaleString('id-ID', { minimumFractionDigits: 2 })}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card">
                  <div className="card-body text-center">
                    <h6 className="card-title">Total Kredit</h6>
                    <h4 className="text-success">Rp {totalKredit.toLocaleString('id-ID', { minimumFractionDigits: 2 })}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className={`card ${Math.abs(balance) <= 0.01 ? 'border-success' : 'border-danger'}`}>
                  <div className="card-body text-center">
                    <h6 className="card-title">Selisih</h6>
                    <h4 className={Math.abs(balance) <= 0.01 ? 'text-success' : 'text-danger'}>
                      Rp {Math.abs(balance).toLocaleString('id-ID', { minimumFractionDigits: 2 })}
                    </h4>
                    {Math.abs(balance) <= 0.01 && <small className="text-success">Balance</small>}
                    {Math.abs(balance) > 0.01 && <small className="text-danger">Tidak Balance</small>}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 text-end">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || Math.abs(balance) > 0.01}
              >
                {loading ? (
                  <><i className="fas fa-spinner fa-spin"></i> Memproses...</>
                ) : (
                  <><i className="fas fa-save"></i> Simpan Jurnal</>
                )}
              </button>
            </div>
          </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default JurnalUmum
